import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useCatalog } from '../data/catalog';
import { useMediaOverrides } from '../utils/media';
import { Kicker, Reveal } from './Reveal';
import { DestinationCard } from './DestinationCard';
import { useLang } from '../i18n';

export const STORIES = [
  { keys: 'story.1', to: '/plan-my-trip', img: '/media/4923818b-7917-45ff-9853-4bb847436818.jpeg' },
  { keys: 'story.2', to: '/destinations/cabo-verde', img: '/media/56467cf4-1c33-4530-bae3-1167dc9c64fa.jpeg' },
  { keys: 'story.3', to: '/destinations/sao-tome-principe/principe', img: 'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?q=80&w=900&auto=format&fit=crop' },
];

export const Marquee = () => {
  const { t } = useLang();
  return (
    <div data-testid="editorial-marquee" className="overflow-hidden border-y border-eco-ink/10 bg-eco-cream py-5">
      <div className="marquee-track flex w-max items-center gap-10 whitespace-nowrap">
        {[0, 1].map((n) => (
          <span key={n} className="flex items-center gap-10 font-serif text-2xl italic text-eco-ink/35">
            <span>{t('marq.1')}</span><span className="text-eco-gold not-italic">·</span>
            <span>{t('marq.2')}</span><span className="text-eco-gold not-italic">·</span>
            <span>{t('marq.3')}</span><span className="text-eco-gold not-italic">·</span>
            <span>São Tomé e Príncipe</span><span className="text-eco-gold not-italic">·</span>
            <span>Cabo Verde</span><span className="text-eco-gold not-italic">·</span>
            <span>Príncipe</span><span className="text-eco-gold not-italic">·</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export const Destinations = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const { destinations } = useCatalog();
  const ov = useMediaOverrides();

  return (
    <>
      <section id="destinos" data-testid="destinations-section" className="scroll-mt-20 bg-eco-cream py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal className="text-center">
            <Kicker>{t('page.destinations.k')}</Kicker>
            <h2 className="eco-section-title mx-auto mt-4 max-w-3xl text-eco-ink">{t('home.dest.title')}</h2>
            <p className="eco-body mx-auto mt-3 max-w-2xl text-eco-ink/60">{t('home.dest.sub')}</p>
          </Reveal>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {destinations.map((d, i) => (
              <Reveal key={d.slug} delay={i * 0.12}>
                <DestinationCard
                  destination={{ ...d, regionName: t(`region.${d.region}`) }}
                  testid={`destination-card-${d.slug}`}
                  statusLabel={d.status === 'available' ? t('dst.available') : d.status === 'partner_network' ? t('dst.partner') : t('dst.coming_soon')}
                  statusClass={d.status === 'available' ? 'bg-eco-green text-white' : d.status === 'partner_network' ? 'bg-eco-gold text-eco-deep' : 'bg-white/20 text-white'}
                  ctaLabel={d.cta}
                />
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2} className="mt-8 text-center">
            <p className="text-sm italic text-eco-ink/50">
              {t('home.dest.note')}
            </p>
          </Reveal>
        </div>
      </section>

      <section id="historias" data-testid="stories-section" className="scroll-mt-20 bg-white py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal className="text-center">
            <Kicker>{t('stories.kicker')}</Kicker>
            <h2 className="mt-4 font-serif text-3xl font-semibold text-eco-ink md:text-4xl">{t('stories.title')}</h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {STORIES.map((s, i) => (
              <Reveal key={s.keys} delay={i * 0.12}>
                <button
                  data-testid={`story-card-${i}`}
                  onClick={() => navigate(s.to)}
                  className="group relative block h-96 w-full overflow-hidden rounded-3xl text-left"
                >
                  <img
                    src={ov[`story:${i}`] || s.img}
                    alt={t(`${s.keys}.t`)}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-eco-deep/90 via-eco-deep/25 to-eco-deep/10 transition-colors duration-500 group-hover:from-eco-deep/95" />
                  <div className="absolute inset-x-0 bottom-0 p-7">
                    <h3 className="font-serif text-2xl font-semibold text-white">{t(`${s.keys}.t`)}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/70">{t(`${s.keys}.x`)}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-eco-gold">
                      {t(`${s.keys}.c`)} <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};
